<template>
    <div class="h-100">
        <div class="h-100" v-if="feedEnabled">
            <grid
                ref="grid"
                v-if="feed.length > 0"
                grid-theme="ag-theme-alpine-partner"
                row-selection="multiple"
                :suppress-row-click-selection="true"
                :column-defs="columnDefs"
                :header-height="50"
                :row-data="feed"
                :is-auto-width="true"
                @selection-changed="handleSelectionChange"
                :master-detail="true"
                :detailCellRenderer="detailCellRenderer"
                :detailCellRendererParams="{
                    onAirbnbListingStatusChange: handleAirbnbListingStatusChange,
                    onRejectedClick: handleAirbnbStatusRejectedClick,
                    onNeedsAttentionClick: handleAirbnbQualityStandardWarnClick,
                    onSuspendedClick: handleAirbnbQualityStandardSuspendClick,
                    onLodgixConnectionStatusClick: handleLodgixConnectionStatusClick,
                }"
            >
                <responsive-button
                    v-if="!hasSelectedRows"
                    :icon="['fas', 'circle-info']"
                    :responsiveness="false"
                    variant="primary"
                    @click="handleInfoClick"
                ></responsive-button>
                <b-button-group v-if="!hasSelectedRows" class="d-none d-lg-flex">
                    <search-multiselect
                        :options="columnOptions"
                        v-model="selectedColumns"
                        class="mr-1"
                        variant="primary"
                        placeholder="Columns"
                        control-value="Columns"
                        item-label="column"
                    ></search-multiselect>
                </b-button-group>
                <responsive-button
                    v-if="!hasSelectedRows"
                    :icon="['fas', 'gear']"
                    @click="handleSettingsClick"
                    variant="primary"
                    >Settings</responsive-button
                >
                <responsive-button
                    v-if="!hasSelectedRows"
                    :icon="['fas', 'star']"
                    @click="handleReviewsClick"
                    variant="primary"
                    >Reviews</responsive-button
                >
                <responsive-button
                    v-if="!hasSelectedRows"
                    :icon="['fas', 'arrows-rotate']"
                    variant="primary"
                    @click="handleRefreshClick"
                    >Refresh</responsive-button
                >
                <responsive-button
                    v-if="allowDisconnect && !hasSelectedRows"
                    :icon="['fas', 'right-from-bracket']"
                    @click="handleDisconnectClick"
                    variant="primary"
                    >Disconnect</responsive-button
                >
                <responsive-button
                    v-if="!messagingApiAllowed && !hasSelectedRows"
                    :icon="['fab', 'airbnb']"
                    :responsiveness="false"
                    @click="handleConnectAirbnbClick"
                    variant="danger"
                    >Authorize Messaging API</responsive-button
                >
                <b-button-group v-if="!hasSelectedRows" class="d-lg-none">
                    <more-filters-dropdown>
                        <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                            <search-multiselect
                                :options="columnOptions"
                                v-model="selectedColumns"
                                class="mr-1"
                                variant="primary"
                                placeholder="Columns"
                                control-value="Columns"
                                item-label="column"
                                @show="handleDropdownShow"
                                @hide="handleDropdownHide"
                            ></search-multiselect>
                        </template>
                    </more-filters-dropdown>
                </b-button-group>
                <bulk-actions
                    v-if="hasSelectedRows"
                    :selected-count="currentSelectedRows.length"
                    :bulk-action-defs="bulkActionDefs"
                />
                <disconnect-modal
                    title="Disconnect Airbnb"
                    description="Are you sure you want to disconnect from Airbnb? All data will be lost!"
                    :modal-id="disconnectModalId"
                    :disconnect="disconnect"
                    @success-disconnect="handleDisconnectSuccess"
                />
                <settings-modal-form
                    :key="settingsModalKey"
                    :modal-id="settingsModalId"
                    :partner-settings="partnerSettings"
                    @success-save="handleSettingsSaveSuccess"
                    @hidden="handleSettingsHidden"
                />
                <occupancy-taxes-modal
                    :modal-id="occupancyTaxesModalId"
                    :key="occupancyTaxesModalKey"
                    :default-occupancy-tax-ids="currentOccupancyTaxIds"
                    :property-count="currentPropertyCount"
                    :set-occupancy-taxes="setOccupancyTaxes"
                    @success-set-occupancy-taxes="handleSetOccupancyTaxesSuccess"
                />
                <create-property-modal
                    :modal-id="createPropertyModalId"
                    :feed-share-id="currentFeedShareId"
                    @update="updateFeed"
                    @refresh="refetchFeedById"
                />
            </grid>
        </div>
        <div v-else-if="busy"></div>
        <div class="mt-1 ml-2" v-else>
            <p>
                Please read our
                <a href="https://support.lodgix.com/en/articles/3323786-airbnb-integration" target="_blank"
                    >full documentation</a
                >
                FIRST before attempting to connect with Airbnb.
            </p>
            <p>
                In addition to any Airbnb host fees, Lodgix will charge 0.5% on all bookings (rental charges only)
                originating from Airbnb.<br />
                This billing is done once per month, and assessed on any Airbnb bookings that were completed in the
                previous month.<br />
                Most will pass all host fees through to the guest using a rate multiplier.
            </p>

            <b-col v-if="allowAirbnb">
                <b-row>
                    <responsive-button
                        :icon="['fab', 'airbnb']"
                        :responsiveness="false"
                        @click="handleConnectAirbnbClick"
                        variant="danger"
                        >Connect with Airbnb</responsive-button
                    >
                </b-row>
            </b-col>
            <b-col v-if="!allowAirbnb && allowAirbnbRequest">
                <b-row>
                    <responsive-button
                        :icon="['fas', 'right-to-bracket']"
                        :responsiveness="false"
                        @click="handleRequestAirbnbAccessClick"
                        variant="primary"
                        >Request Airbnb Publish Access</responsive-button
                    >
                </b-row>
            </b-col>
            <span v-if="!allowAirbnbRequest && !allowAirbnb">
                Airbnb access request was sent. Please wait for review.
            </span>
        </div>
        <modal
            :id="requestAirbnbAccessModalId"
            title="Request Airbnb Publish Access"
            ok-title="Request Access"
            @ok="requestAirbnbAccess"
            :ok-disabled="busy"
        >
            <span
                >Requesting access is required to publish properties to Airbnb. Click the "Request Access" button below
                and someone from Lodgix will review your request. Please come back later to see if your request has been
                approved.</span
            >
        </modal>
        <modal
            :id="connectAirbnbModalId"
            title="Airbnb.com Agreement"
            ok-title="Agree"
            size="xl"
            @ok="connectAirbnb"
            :ok-disabled="busy"
        >
            <terms-and-conditions />
        </modal>
        <modal :id="infoModalId" hide-header ok-only>
            <div>
                Feed enabled on: {{ feedEnabledOn }} for Airbnb host ID:
                {{ accountId }}
            </div>
            <a href="#" @click="handleAgreementClick">Terms and conditions</a>
            agreed on: {{ feedEnabledOn }}
        </modal>
        <modal :id="agreementModalId" title="Airbnb.com Agreement" ok-only size="xl">
            <terms-and-conditions />
        </modal>
        <modal
            :id="connectAirbnbDuplicateErrorModal"
            title="Duplicate Error"
            ok-only
            header-bg-variant="danger"
            header-text-variant="white"
        >
            An error has occurred while connecting to Airbnb. Please confirm that you aren't already connected to
            another Lodgix account in your
            <a target="_blank" href="https://www.airbnb.com/account-settings/privacy-and-sharing"
                >Airbnb Connected Apps</a
            >.
        </modal>
        <modal
            :id="syncErrorModalId"
            :title="syncErrorTitle"
            header-bg-variant="danger"
            header-text-variant="white"
            ok-only
        >
            <template v-if="syncErrorType === 'sync'">
                <div><b>Airbnb Response:</b></div>
                <div
                    :class="{
                        'mb-4': syncErrorModalIsAddressError,
                    }"
                >
                    {{ syncErrorModalErrorDate }} - {{ syncErrorModalErrorMessage }}
                </div>
                <template v-if="syncErrorModalIsAddressError">
                    <div><b>Airbnb Address:</b></div>
                    <div v-if="syncErrorModalAddress">
                        <span>
                            Address1: {{ syncErrorModalAddress.address1 }}<br />
                            Address2: {{ syncErrorModalAddress.address2 }}<br />
                            City: {{ syncErrorModalAddress.city }}<br />
                            Zip: {{ syncErrorModalAddress.zip }}<br />
                            State: {{ syncErrorModalAddress.state }}<br />
                            Country: {{ syncErrorModalAddress.country }}<br />
                            Latitude: {{ syncErrorModalAddress.latitude }}<br />
                            Longitude: {{ syncErrorModalAddress.longitude }}<br />
                        </span>
                    </div>
                    <div v-else>
                        <span>
                            An error occurred while fetching listing address. Please try again later. <br />
                            If problem persists, please check your Airbnb connection.
                        </span>
                    </div>
                </template>
            </template>
            <template v-else>
                <div>{{ rejectedModalMessage }}</div>
            </template>
        </modal>
        <modal
            :id="airbnbQualityStandardWarnModalId"
            title="Needs Attention"
            header-bg-variant="warning"
            header-text-variant="white"
            ok-only
        >
            The listing is at risk of being suspended due to
            <a href="https://www.airbnb.com/help/article/2895" target="_blank">ground rules</a> violations. To review
            the issues, go to the
            <a
                :href="`https://www.airbnb.com/insights/listing-issues/${airbnbQualityStandardModalData.listingId}`"
                target="_blank"
                >Listing Issues page</a
            >.
        </modal>
        <modal
            :id="airbnbQualityStandardSuspendModalId"
            title="Suspended"
            header-bg-variant="danger"
            header-text-variant="white"
            ok-only
        >
            The listing was suspended for Trust and Safety concerns.
            <template v-if="airbnbQualityStandardModalData.restrictedCapabilities.length > 0">
                Restricted capabilities:
                <ul>
                    <li
                        v-for="restrictedCapability in airbnbQualityStandardModalData.restrictedCapabilities"
                        :key="restrictedCapability.id"
                    >
                        {{ restrictedCapability.description }}
                    </li>
                </ul>
            </template>
            To review the issues that led to your suspension, go to the
            <a
                :href="`https://www.airbnb.com/insights/listing-issues/${airbnbQualityStandardModalData.listingId}`"
                target="_blank"
                >Listing Issues page</a
            >. You should also familiarize yourself with Airbnb
            <a href="https://www.airbnb.com/help/article/2895" target="_blank">ground rules</a> for Hosts.
        </modal>
        <validation-observer v-slot="{ meta }">
            <modal ok-title="Save" :id="nameModalId" title="Airbnb Custom Property Name" :ok-disabled="!meta.valid">
                <span v-if="currentNameFeedShare !== null">
                    <validation-provider
                        name="custom_property_name"
                        v-slot="{ errors, handleChange }"
                        :value="currentNameFeedShare.airbnbPropertyName"
                        rules="required|maxMessageLen:50,Name"
                        validateOnMount
                    >
                        <form-input
                            floating-label="Airbnb Custom Property Name"
                            :leftIcon="['fas', 'arrow-right']"
                            v-model="currentNameFeedShare.airbnbPropertyName"
                            :error="errors[0]"
                            @input="handleChange"
                        ></form-input>
                    </validation-provider>
                </span>
                <template #modal-footer="{ cancel }">
                    <b-button variant="secondary" @click="cancel()"> Cancel </b-button>
                    <b-button
                        variant="success"
                        v-if="currentNameFeedShare.custom_property_name !== null"
                        @click="handleSaveCustomName(null)"
                    >
                        Remove Custom Name
                    </b-button>
                    <b-button
                        variant="primary"
                        :disabled="!meta.valid"
                        @click="handleSaveCustomName(currentNameFeedShare.airbnbPropertyName)"
                    >
                        Save Custom Name
                    </b-button>
                </template>
            </modal>
        </validation-observer>
        <de-activation-modal
            :property-id="
                airbnbListingStatusModalData.feedShare ? airbnbListingStatusModalData.feedShare.rental_property.id : 0
            "
            :modal-id="airbnbUnlistListingModalId"
            action="unlist"
            @ok="unlistAirbnbListing"
        />
        <modal :id="airbnbListListingModalId" title="List Property" @ok="listAirbnbListing">
            Are you sure you want to list the property on Airbnb? Your property will appear in search results and can be
            booked by guests through Airbnb.
        </modal>
        <modal
            :id="lodgixConnectionStatusModalId"
            title="Change Synchronization"
            ok-title="Proceed"
            :ok-disabled="
                lodgixConnectionStatusModalData.synchronizationCategory ===
                lodgixConnectionStatusModalData.currentSynchronizationCategory
            "
            @ok="
                () =>
                    lodgixConnectionStatusModalData.currentSynchronizationCategory ===
                    PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_NOT_CONNECTED
                        ? confirmDisconnectProperty()
                        : changeLodgixConnectionStatus()
            "
        >
            <b-form-radio
                class="mb-2"
                v-model="lodgixConnectionStatusModalData.currentSynchronizationCategory"
                name="synchronization-category"
                :value="PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_SYNC_EVERYTHING"
            >
                <div>Sync Everything (recommended)</div>
                <div class="text-secondary">
                    Sync all listing details, booking settings, pricing, availability, etc., with Lodgix.
                </div>
            </b-form-radio>
            <b-form-radio
                class="mb-2"
                v-model="lodgixConnectionStatusModalData.currentSynchronizationCategory"
                name="synchronization-category"
                :value="PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_LIMITED_SYNC"
            >
                <div>Limited Sync</div>
                <div class="text-secondary">
                    Sync only pricing and availability with Lodgix. All other listing content will be managed on Airbnb.
                </div>
            </b-form-radio>
            <b-form-radio
                v-model="lodgixConnectionStatusModalData.currentSynchronizationCategory"
                name="synchronization-category"
                :value="PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_NOT_CONNECTED"
            >
                <div class="text-danger">Disconnect</div>
                <div class="text-secondary">
                    Once a property is disconnected, it will no longer synchronize with Airbnb.
                </div>
            </b-form-radio>
        </modal>
        <modal
            :id="disconnectPropertyModalId"
            title="Disconnect property"
            ok-title="Disconnect"
            @ok="changeLodgixConnectionStatus"
        >
            This property is currently integrated / sync'd to Airbnb. Are you sure you want to disconnect the property
            from Airbnb?
        </modal>
        <modal
            :id="turnOffInstantBookingModalId"
            title="Turn off Instant Booking"
            ok-title="Turn Instant Booking Off"
            @ok="turnOffInstantBooking"
        >
            Are you sure you want to turn off Instant Booking?
            <div class="mt-4 text-secondary">If so, you'll need to keep these things in mind:</div>
            <ul class="mt-2 p-0">
                <li class="d-flex">
                    <font-awesome-icon class="mt-1" size="xl" :icon="['fas', 'triangle-exclamation']" />
                    <div class="ml-3 flex flex-column">
                        <div class="font-weight-bold">You may get fewer bookings</div>
                        <div class="text-secondary">
                            Instant Booking listings are shown higher in search results and can reach more guests.
                        </div>
                    </div>
                </li>
                <li class="mt-2 d-flex">
                    <font-awesome-icon class="mt-1" size="xl" :icon="['fas', 'message']" />
                    <div class="ml-3 flex flex-column">
                        <div class="font-weight-bold">You'll need to review every booking request</div>
                        <div class="text-secondary">
                            With Instant Booking off, bookings won't be accepted automatically.
                        </div>
                    </div>
                </li>
                <li class="mt-2 d-flex">
                    <font-awesome-icon class="mt-1" size="xl" :icon="['fas', 'clock']" />
                    <div class="ml-3 flex flex-column">
                        <div class="font-weight-bold">You'll need to respond to each request in 24 hours</div>
                        <div class="text-secondary">Late responses can impact your position in search results.</div>
                    </div>
                </li>
            </ul>
        </modal>
    </div>
</template>

<script>
    import difference from 'lodash/difference';
    import isNil from 'lodash/isNil';
    import range from 'lodash/range';
    import API from '../../../api';
    import { mapGetters } from 'vuex';
    import {
        PARTNER_AIRBNB_CANCELLATION_POLICY_MAPPING,
        PARTNER_AIRBNB_LONG_TERM_CANCELLATION_POLICY_MAPPING,
        PARTNER_AIRBNB_INSTANT_BOOKING_CATEGORY_MAPPING,
    } from '../../../constants';
    import {
        PARTNER_AIRBNB_DATA_ENDPOINT,
        PARTNER_AIRBNB_FEED_SHARE_BULK_MARKETING_TITLE_ENABLE,
        PARTNER_AIRBNB_FEED_SHARE_BULK_MARKETING_TITLE_DISABLE,
        PARTNER_AIRBNB_FEED_SHARE_BULK_OCCUPANCY_TAX_SET,
        PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT,
        PARTNER_AIRBNB_FEED_SHARE_FEED_ENDPOINT,
        PARTNER_AIRBNB_SETTINGS_ENDPOINT,
        PARTNER_AIRBNB_DISCONNECT_ENDPOINT,
        PARTNER_AIRBNB_REQUEST_ACCESS_ENDPOINT,
        PARTNER_AIRBNB_FEED_SHARE_LIST_PROPERTY,
        PARTNER_AIRBNB_FEED_SHARE_UNLIST_PROPERTY,
        PARTNER_AIRBNB_FEED_SHARE_UPDATE_SYNC_MODE_PROPERTY,
        PARTNER_AIRBNB_FEED_SHARE_SYNC_ERROR_ENDPOINT,
        PARTNER_AIRBNB_FEED_SHARE_SYNC_PROPERTY_ENDPOINT,
        PARTNER_ARIBNB_FEED_SHARE_CANCELLATION_POLICIES_ENDPOINT,
    } from '../../../endpoints';
    import { formatResponseError } from '../../../utils/api';
    import { isMediumScreen } from '../../../utils/screens';
    import { toAmountDisplay } from '../../../utils/numbers';
    import { escapeHTML } from '../../../utils/strings';
    import { extractHTMLContent } from '../../../utils/strings';
    import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import BulkActions from '../../generic/bulk-actions/bulk-actions.vue';
    import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import Grid from '../../generic/grid/grid.vue';
    import Modal from '../../generic/modal/modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import DeActivationModal from '../../common/de-activation-modal.vue';
    import DetailCellRenderer from './detail-cell-renderer.vue';
    import DisconnectModal from './disconnect-modal.vue';
    import OccupancyTaxesModal from './occupancy-taxes/occupancy-taxes-modal.vue';
    import CreatePropertyModal from './create-property-modal.vue';
    import SettingsModalForm from './settings-modal-form.vue';
    import StatusCellRenderer from './status-cell-renderer.vue';
    import StatusIconCellRenderer from './status-icon-cell-renderer.vue';
    import TermsAndConditions from './terms-and-conditions.vue';
    import NoteCellRenderer from './note-cell-renderer.vue';
    import NameCellRenderer from './name-cell-renderer.vue';

    import {
        PARTNER_AIRBNB_COLUMN_BASE_GUESTS,
        PARTNER_AIRBNB_COLUMN_CANCEL_POLICY,
        PARTNER_AIRBNB_COLUMN_LONG_TERM_CANCEL_POLICY,
        PARTNER_AIRBNB_COLUMN_CLEANING_FEE,
        PARTNER_AIRBNB_COLUMN_SHORT_TERM_CLEANING_FEE,
        PARTNER_AIRBNB_COLUMN_COMMUNITY_FEE,
        PARTNER_AIRBNB_COLUMN_PET_FEE,
        PARTNER_AIRBNB_COLUMN_INSTANT_BOOKING,
        PARTNER_AIRBNB_COLUMN_GUEST_RATE,
        PARTNER_AIRBNB_COLUMN_MANAGEMENT_FEE,
        PARTNER_AIRBNB_COLUMN_MARKETING_TITLE,
        PARTNER_AIRBNB_COLUMN_NO_REFUND_DISCOUNT,
        PARTNER_AIRBNB_COLUMN_OCCUPANCY_TAXES,
        PARTNER_AIRBNB_COLUMN_OTHER_THINGS_TO_NOTE,
        PARTNER_AIRBNB_COLUMN_RATE_MULTIPLIER,
        PARTNER_AIRBNB_COLUMN_MAPPING,
        PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_NOT_CONNECTED,
        PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_SYNC_EVERYTHING,
        PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_LIMITED_SYNC,
    } from './constants';

    export default {
        name: 'PartnerAirbnb',
        components: {
            BulkActions,
            DeActivationModal,
            Grid,
            ResponsiveButton,
            MoreFiltersDropdown,
            SearchMultiselect,
            SettingsModalForm,
            DisconnectModal,
            OccupancyTaxesModal,
            CreatePropertyModal,
            Modal,
            TermsAndConditions,
            FormInput,
            statusCellRenderer: StatusCellRenderer,
            statusIconCellRenderer: StatusIconCellRenderer,
            noteCellRenderer: NoteCellRenderer,
            actionsCellRenderer: ActionsCellRenderer,
            nameCellRenderer: NameCellRenderer,
            DetailCellRenderer,
        },
        mounted() {
            if (this.$route.query.error) {
                if (this.$route.query.error === 'duplicate') {
                    this.$bvModal.show(this.connectAirbnbDuplicateErrorModal);
                } else if (this.$route.query.error === 'authorize') {
                    this.errorToast({
                        text: 'An error has occurred while connecting to Airbnb.',
                    });
                }

                this.$router.push('/app/partner/airbnb/');
            }

            this.load();
        },
        computed: {
            ...mapGetters('header', ['userID']),
            longTermCancellationPolicyOptions() {
                return Object.entries(PARTNER_AIRBNB_LONG_TERM_CANCELLATION_POLICY_MAPPING).map(
                    ([policy, policyText]) => ({
                        value: policy,
                        text: policyText,
                    })
                );
            },
            columnOptions() {
                return Object.entries(PARTNER_AIRBNB_COLUMN_MAPPING).map(([column, columnName]) => ({
                    value: column,
                    text: columnName,
                }));
            },
            extraPersonBaseOptions() {
                return [{ value: 0, text: 'Disabled' }, ...range(1, 100).map((n) => ({ value: n, text: n }))];
            },
            hasSelectedRows() {
                return this.currentSelectedRows.length > 0;
            },
            currentOccupancyTaxIds() {
                if (!!this.currentFeedShare) {
                    return this.currentFeedShare.occupancy_taxes.map((tax) => tax.id);
                }
                return [];
            },
            currentPropertyCount() {
                if (!!this.currentFeedShare) {
                    return 1;
                }
                if (this.hasSelectedRows) {
                    return this.currentSelectedRows.length;
                }
                return 0;
            },
            isMobile() {
                const md = 768;
                return window.innerWidth < md || window.innerHeight < md;
            },
            columnDefs() {
                return [
                    {
                        field: '',
                        headerName: '',
                        maxWidth: 20,
                        minWidth: 20,
                        resizable: false,
                        pinned: 'left',
                        sortable: false,
                        cellStyle: { display: 'flex', justifyContent: 'center', paddingRight: 0 },
                        cellRenderer: 'statusIconCellRenderer',
                    },
                    {
                        field: 'rental_property.id',
                        headerName: 'P. ID',
                        minWidth: 60,
                        pinned: 'left',
                        headerCheckboxSelection: true,
                        headerCheckboxSelectionFilteredOnly: true,
                        checkboxSelection: true,
                        cellRenderer: 'agGroupCellRenderer',
                        valueParser: (data) => data.rental_property.id,
                    },
                    {
                        field: 'rental_property.name',
                        headerName: 'P. Name',
                        suppressSizeToFit: true,
                        pinned: 'left',
                        minWidth: 80,
                        width: isMediumScreen() ? undefined : 80,
                        autoWidthExclude: this.isMobile,
                        cellRenderer: 'nameCellRenderer',
                        cellRendererParams: {
                            handleNameClick: this.handleAirbnbNameClick,
                        },
                        valueParser: (data) => data.rental_property.name,
                    },
                    {
                        type: 'switchColumn',
                        field: 'use_marketing_title',
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_MARKETING_TITLE],
                        cellClass: 'd-flex justify-content-center',
                        headerClass: 'ag-centered-header',
                        minWidth: 85,
                        sortable: false,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_MARKETING_TITLE),
                        valueParser: (data) => data.use_marketing_title,
                        cellRendererParams: ({ data }) => {
                            return {
                                onChange: async ({ data, node, api }, value) => {
                                    try {
                                        let response = await API.patch(
                                            PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id),
                                            {
                                                use_marketing_title: value,
                                            }
                                        );
                                        node.data = response.data;
                                        const nodesToRefresh = [node];
                                        if (node.detailNode) {
                                            nodesToRefresh.push(node.node.detailNode);
                                        }
                                        api.refreshCells({ rowNodes: nodesToRefresh, force: true });
                                    } catch (e) {
                                        this.fieldBasedErrorToast({
                                            error: e,
                                            field: 'use_marketing_title',
                                            defaultText: 'Could not save marketing title.',
                                        });
                                        const nodesToRefresh = [node];
                                        if (node.detailNode) {
                                            nodesToRefresh.push(node.node.detailNode);
                                        }
                                        api.redrawRows({ rowNodes: nodesToRefresh });
                                        return;
                                    }
                                },
                                disabled: data.custom_property_name !== null,
                            };
                        },
                    },
                    {
                        field: 'cancellation_policy',
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_CANCEL_POLICY],
                        cellClass: 'lx-editable-cell text-primary',
                        cellEditor: 'selectCellEditor',
                        editable: true,
                        valueParser: (data) => data.cancellation_policy,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_CANCEL_POLICY),
                        cellRenderer: ({ data }) =>
                            (data && this.getCancellationPolicyText(data.cancellation_policy)) || 'Not set',
                        cellEditorParams: ({ data }) => ({
                            options: async () => {
                                try {
                                    const response = await API.get(
                                        PARTNER_ARIBNB_FEED_SHARE_CANCELLATION_POLICIES_ENDPOINT(data.id),
                                    );
                                    return response.data.cancellation_policies.map((policy) => ({
                                        value: policy,
                                        text: this.getCancellationPolicyText(policy),
                                    }));
                                } catch (e) {
                                    const { generic } = formatResponseError(e);
                                    this.errorToast({
                                        text: generic || 'Could not fetch cancellation policies.',
                                    });
                                    return [];
                                }
                            },
                        }),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        cancellation_policy: newValue,
                                    });
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'cancellation_policy',
                                        defaultText: 'Could not save cancellation policy.',
                                    });

                                    data.cancellation_policy = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.cancellation_policy = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'long_term_cancellation_policy',
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_LONG_TERM_CANCEL_POLICY],
                        cellClass: 'lx-editable-cell text-primary',
                        cellEditor: 'selectCellEditor',
                        editable: true,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_LONG_TERM_CANCEL_POLICY),
                        valueParser: (data) => data.long_term_cancellation_policy,
                        cellRenderer: ({ data }) =>
                            (data && this.getLongTermCancellationPolicyText(data.long_term_cancellation_policy)) ||
                            'Not set',
                        cellEditorParams: () => ({
                            options: this.longTermCancellationPolicyOptions,
                        }),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        long_term_cancellation_policy: newValue,
                                    });
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'long_term_cancellation_policy',
                                        defaultText: 'Could not save long-term cancellation policy.',
                                    });

                                    data.long_term_cancellation_policy = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.long_term_cancellation_policy = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'non_refundable_discount',
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_NO_REFUND_DISCOUNT],
                        type: 'editableColumn',
                        cellClass: 'd-flex lx-editable-cell justify-content-center text-primary',
                        headerClass: 'ag-centered-header',
                        cellEditorParams: {
                            isDecimal: true,
                            select: true,
                            empty: true,
                            emptyValue: null,
                        },
                        sortable: false,
                        minWidth: 65,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_NO_REFUND_DISCOUNT),
                        valueParser: (data) => data.no_refund_discount,
                        cellRenderer: ({ value }) => {
                            if (isNil(value)) {
                                return 'Not set';
                            }
                            return toAmountDisplay({
                                amount: value,
                                percentage: true,
                            });
                        },
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        non_refundable_discount: newValue,
                                    });
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'non_refundable_discount',
                                        defaultText: 'Could not save discount.',
                                    });

                                    data.non_refundable_discount = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.non_refundable_discount = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'occupancy_taxes',
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_OCCUPANCY_TAXES],
                        cellClass: 'lx-editable-cell text-primary',
                        autoHeight: true,
                        sortable: false,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_OCCUPANCY_TAXES),
                        getQuickFilterText: ({ data }) =>
                            data && data.occupancy_taxes.map((item) => item.name).join(' '),
                        cellRenderer: ({ data }) =>
                            ((data && data.occupancy_taxes) || []).length > 0
                                ? data.occupancy_taxes.map((item) => escapeHTML(item.name)).join('<br />')
                                : 'Not set',
                        onCellClicked: this.handleOccupancyTaxesCellClick,
                        valueParser: (data) => data.occupancy_taxes,
                    },
                    {
                        field: 'rental_property.notes',
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_OTHER_THINGS_TO_NOTE],
                        cellRenderer: 'noteCellRenderer',
                        headerClass: 'ag-centered-header',
                        minWidth: 70,
                        maxWidth: 70,
                        sortable: false,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_OTHER_THINGS_TO_NOTE),
                        valueParser: (data) => data.rental_property.notes,
                    },
                    {
                        field: 'instant_booking_allowed_category',
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_INSTANT_BOOKING],
                        cellClass: 'lx-editable-cell text-primary',
                        cellEditor: 'selectCellEditor',
                        editable: true,
                        valueParser: (data) => data.instant_booking_allowed_category,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_INSTANT_BOOKING),
                        cellRenderer: ({ data }) =>
                            data && this.getInstantBookingCategoryText(data.instant_booking_allowed_category),
                        cellEditorParams: () => ({
                            options: this.instantBookingCategoryOptions,
                        }),
                        valueSetter: ({ node, data, newValue, oldValue }) => {
                            if (
                                this.instantBookingCategoryOptions[this.instantBookingCategoryOptions.length - 1]
                                    .value === newValue
                            ) {
                                this.turnOffInstantBookingModalData.node = node;
                                this.turnOffInstantBookingModalData.feedShare = data;
                                this.turnOffInstantBookingModalData.value = newValue;
                                this.turnOffInstantBookingModalData.oldValue = oldValue;
                                this.$bvModal.show(this.turnOffInstantBookingModalId);
                                return false;
                            } else {
                                (async () => {
                                    try {
                                        await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                            instant_booking_allowed_category: newValue,
                                        });
                                    } catch (e) {
                                        this.fieldBasedErrorToast({
                                            error: e,
                                            field: 'instant_booking_allowed_category',
                                            defaultText: 'Could not save instant booking.',
                                        });

                                        data.instant_booking_allowed_category = oldValue;
                                        node.setData(data);
                                    }
                                })();
                                data.instant_booking_allowed_category = newValue;
                                return true;
                            }
                        },
                    },
                    {
                        field: 'rate_multiplier',
                        valueParser: (data) => data.rate_multiplier,
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_RATE_MULTIPLIER],
                        type: 'editableColumn',
                        cellClass: 'lx-editable-cell text-primary d-flex justify-content-center',
                        headerClass: 'ag-centered-header',
                        cellEditorParams: {
                            isDecimal: true,
                            select: true,
                            empty: true,
                            emptyValue: null,
                            maxDecimalPlaces: 4,
                        },
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_RATE_MULTIPLIER),
                        sortable: false,
                        minWidth: 80,
                        cellRenderer: ({ value }) => {
                            if (isNil(value)) {
                                return 'Not set';
                            }
                            return toAmountDisplay({
                                amount: value,
                                decimalPlaces: 4,
                            });
                        },
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        rate_multiplier: newValue,
                                    });
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'rate_multiplier',
                                        defaultText: 'Could not save multiplier.',
                                    });

                                    data.rate_multiplier = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.rate_multiplier = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'extra_person_base',
                        valueParser: (data) => data.extra_person_base,
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_BASE_GUESTS],
                        cellClass: 'lx-editable-cell ag-right-aligned-cell text-primary',
                        headerClass: 'ag-right-aligned-header',
                        cellEditor: 'selectCellEditor',
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_BASE_GUESTS),
                        editable: true,
                        minWidth: 75,
                        cellRenderer: ({ data }) => (!!data ? data.extra_person_base || 'Disabled' : ''),
                        cellEditorParams: () => ({
                            options: this.extraPersonBaseOptions,
                        }),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        extra_person_base: newValue,
                                    });
                                    this.refetchFeed();
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'extra_person_base',
                                        defaultText: 'Could not save base guests.',
                                    });

                                    data.extra_person_base = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.extra_person_base = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'extra_person_rate',
                        valueParser: (data) => data.extra_person_rate,
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_GUEST_RATE],
                        type: 'editableColumn',
                        cellClass: ({ data }) =>
                            data.extra_person_base
                                ? 'lx-editable-cell ag-right-aligned-cell text-primary'
                                : 'ag-right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        cellEditorParams: {
                            isNumeric: true,
                            select: true,
                            empty: true,
                            emptyValue: 0,
                        },
                        minWidth: 65,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_GUEST_RATE),
                        cellRenderer: ({ data, value }) => {
                            if (!data.extra_person_base) {
                                return 'Not set';
                            }
                            return toAmountDisplay({ amount: value, integer: true });
                        },
                        editable: ({ data }) => !!data.extra_person_base,
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    if (newValue === null) newValue = 0;
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        extra_person_rate: newValue,
                                    });
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'extra_person_rate',
                                        defaultText: 'Could not save additional guest rate.',
                                    });

                                    data.extra_person_rate = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.extra_person_rate = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'cleaning_fee',
                        headerName: this.addDamageToCleaningFee ? 'Cleaning Fee + Damage Protection' : 'Cleaning Fee',
                        headerName: this.addDamageToCleaningFee
                            ? PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_CLEANING_FEE] + ' + Damage Protection'
                            : PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_CLEANING_FEE],
                        type: 'editableColumn',
                        sortable: false,
                        cellClass: 'd-flex justify-content-center',
                        headerClass: 'ag-centered-header',
                        cellEditorParams: {
                            isNumeric: true,
                            select: true,
                            empty: true,
                            emptyValue: 0,
                        },
                        minWidth: this.addDamageToCleaningFee ? 100 : 75,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_CLEANING_FEE),
                        valueParser: (data) => data.cleaning_fee,
                        cellRenderer: ({ data, value }) => {
                            const cleaningFeeDisplay = toAmountDisplay({ amount: value, integer: true });
                            let html = `
                            <span class="lx-editable-cell text-primary ag-cell-value">${cleaningFeeDisplay}</span>
                        `;
                            if (this.addDamageToCleaningFee) {
                                const damageProtectionFeeDisplay = toAmountDisplay({
                                    amount: data.damage_protection_fee,
                                    integer: true,
                                });
                                html += `<span> + ${damageProtectionFeeDisplay}</span>`;
                            }
                            return html;
                        },
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        cleaning_fee: newValue,
                                    });
                                } catch (e) {
                                    const { generic } = formatResponseError(e);
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'cleaning_fee',
                                        defaultText: 'Could not save cleaning fee.',
                                    });

                                    data.cleaning_fee = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.cleaning_fee = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'short_term_cleaning_fee',
                        valueParser: (data) => data.short_term_cleaning_fee,
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_SHORT_TERM_CLEANING_FEE],
                        type: 'editableColumn',
                        sortable: false,
                        cellClass: 'lx-editable-cell text-primary d-flex justify-content-center',
                        headerClass: 'ag-centered-header',
                        cellEditorParams: {
                            isNumeric: true,
                            select: true,
                            empty: true,
                            emptyValue: null,
                        },
                        minWidth: 90,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_SHORT_TERM_CLEANING_FEE),
                        cellRenderer: ({ value }) => {
                            if (isNil(value)) {
                                return 'Not set';
                            }
                            return toAmountDisplay({ amount: value, integer: true });
                        },
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        short_term_cleaning_fee: newValue,
                                    });
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'short_term_cleaning_fee',
                                        defaultText: 'Could not save short-term cleaning fee.',
                                    });

                                    data.short_term_cleaning_fee = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.short_term_cleaning_fee = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'mgmt_fee',
                        valueParser: (data) => data.mgmt_fee,
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_MANAGEMENT_FEE],
                        type: 'editableColumn',
                        cellClass: 'lx-editable-cell text-primary d-flex justify-content-center',
                        headerClass: 'ag-centered-header',
                        cellEditorParams: {
                            isNumeric: true,
                            select: true,
                            empty: true,
                            emptyValue: 0,
                        },
                        minWidth: 60,
                        sortable: false,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_MANAGEMENT_FEE),
                        cellRenderer: ({ value }) => {
                            return toAmountDisplay({ amount: value, integer: true });
                        },
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    if (newValue === null) newValue = 0;
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        mgmt_fee: newValue,
                                    });
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'mgmt_fee',
                                        defaultText: 'Could not save management fee.',
                                    });

                                    data.mgmt_fee = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.mgmt_fee = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'community_fee',
                        valueParser: (data) => data.community_fee,
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_COMMUNITY_FEE],
                        type: 'editableColumn',
                        cellClass: 'lx-editable-cell text-primary d-flex justify-content-center',
                        headerClass: 'ag-centered-header',
                        cellEditorParams: {
                            isDecimal: true,
                            select: true,
                            empty: true,
                            emptyValue: 0,
                        },
                        minWidth: 90,
                        sortable: false,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_COMMUNITY_FEE),
                        cellRenderer: ({ value }) => {
                            return toAmountDisplay({
                                amount: value,
                                percentage: true,
                            });
                        },
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    if (newValue === null) newValue = 0;
                                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                        community_fee: newValue,
                                    });
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'community_fee',
                                        defaultText: 'Could not save community fee.',
                                    });

                                    data.community_fee = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.community_fee = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'pet_fee',
                        valueParser: (data) => data.pet_fee,
                        headerName: PARTNER_AIRBNB_COLUMN_MAPPING[PARTNER_AIRBNB_COLUMN_PET_FEE],
                        type: 'editableColumn',
                        sortable: false,
                        cellClass: ({ data }) =>
                            data.pet_fee_allowed
                                ? 'lx-editable-cell d-flex justify-content-center text-primary'
                                : 'd-flex justify-content-center',
                        headerClass: 'ag-centered-header',
                        cellEditorParams: {
                            isNumeric: true,
                            select: true,
                            empty: true,
                            emptyValue: null,
                        },
                        minWidth: 60,
                        hide: this.isColumnHidden(PARTNER_AIRBNB_COLUMN_PET_FEE),
                        editable: ({ data }) => {
                            return data.pet_fee_allowed;
                        },
                        cellRenderer: ({ data, value }) => {
                            if (data.pet_fee_allowed) {
                                if (isNil(value)) {
                                    return 'Not set';
                                }
                                return toAmountDisplay({ amount: value, integer: true });
                            }
                            return 'Not allowed';
                        },
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    const { data: feedShare } = await API.patch(
                                        PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(data.id),
                                        {
                                            pet_fee: newValue,
                                        }
                                    );
                                    data.pet_fee = feedShare.pet_fee;
                                    node.setData(data);
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'pet_fee',
                                        defaultText: 'Could not save pet fee.',
                                    });

                                    data.pet_fee = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.pet_fee = newValue;
                            return true;
                        },
                    },
                    {
                        field: 'remote_id',
                        valueParser: (data) => data.remote_id,
                        headerName: 'Airbnb ID',
                        cellRenderer: ({ data }) => {
                            if (!data) {
                                return;
                            }
                            if (data.remote_id != null) {
                                return `<a
                                        target="_blank"
                                        title="${data.remote_id}"
                                        href="https://www.airbnb.com/rooms/${data.remote_id}"
                                    >
                                        ${data.remote_id}
                                    </a>`;
                            }
                            return '';
                        },
                    },
                    {
                        field: 'airbnb_status.status',
                        headerName: 'Status',
                        cellRenderer: 'statusCellRenderer',
                        cellRendererParams: {
                            onRejectedClick: this.handleAirbnbStatusRejectedClick,
                            onSyncErrorClick: this.handleAirbnbSyncErrorClick,
                            onNeedsAttentionClick: this.handleAirbnbQualityStandardWarnClick,
                            onSuspendedClick: this.handleAirbnbQualityStandardSuspendClick,
                        },
                        autoWidthExclude: true,
                        valueParser: (data) => data.airbnb_status.status,
                    },
                    {
                        headerName: '',
                        maxWidth: 60,
                        minWidth: 60,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: ({ node }) => {
                            return {
                                create: (feedShare) => {
                                    this.currentFeedShareId = feedShare.id;
                                    this.currentNode = node;
                                    console.log(this.createPropertyModalId);
                                    this.$bvModal.show(this.createPropertyModalId);
                                },
                                update: async (feedShare) => {
                                    this.$parent.$emit('busy', true);
                                    const gridApi = this.$refs.grid.gridOptions.api;
                                    try {
                                        const { data } = await API.post(
                                            PARTNER_AIRBNB_FEED_SHARE_SYNC_PROPERTY_ENDPOINT(feedShare.id)
                                        );
                                        node.setData(data);
                                        if (node.detailNode) {
                                            gridApi.redrawRows({ rowNodes: [node.detailNode] });
                                        }
                                        this.successToast({ text: 'Airbnb property was updated.' });
                                    } catch (error) {
                                        const { generic } = formatResponseError(error);
                                        if (generic && generic.includes(this.limitedSyncError)) {
                                            this.errorToast({ text: this.limitedSyncErrorMessage });
                                        } else {
                                            this.errorToast({ text: generic || `Could not update property.` });
                                        }
                                        try {
                                            const { data } = await this.fetchFeedById(feedShare.id);
                                            node.setData(data);
                                            if (node.detailNode) {
                                                gridApi.redrawRows({ rowNodes: [node.detailNode] });
                                            }
                                        } catch (e) {
                                            const { generic } = formatResponseError(e);
                                            this.errorToast({ text: generic || 'Could not load airbnb feed.' });
                                        }
                                        return;
                                    } finally {
                                        this.$parent.$emit('busy', false);
                                    }
                                },
                            };
                        },
                    },
                ];
            },
        },
        data() {
            const defaultColumnVisibilityMapping =
                this.$store.getters['partnerAirbnbColumnsLocalStorage/columnVisibilityMapping'];
            return {
                busy: false,
                accountId: '',
                feedEnabled: false,
                feedEnabledOn: null,
                feed: [],
                messagingApiAllowed: false,
                addDamageToCleaningFee: false,
                allowAirbnb: false,
                allowAirbnbRequest: false,
                allowDisconnect: false,
                airbnbClientId: '',
                airbnbRedirectURL: '',
                partnerSettings: null,
                createPropertyModalId: 'airbnb-create-property-modal',
                settingsModalId: 'settings-modal',
                settingsModalKey: 100,
                agreementModalId: 'agreement-modal',
                connectAirbnbModalId: 'connect-airbnb-modal',
                connectAirbnbDuplicateErrorModal: 'connect-airbnb-duplicate-error-modal',
                disconnectModalId: 'disconnect-modal',
                nameModalId: 'name-modal',
                occupancyTaxesModalId: 'occupancy-taxes-modal',
                occupancyTaxesModalKey: 1,
                infoModalId: 'info-modal',
                requestAirbnbAccessModalId: 'request-airbnb-access-modal',
                syncErrorModalId: 'sync-error-modal',
                syncErrorModalErrorMessage: '',
                syncErrorModalErrorDate: null,
                syncErrorModalAddress: null,
                syncErrorModalIsAddressError: null,
                rejectedModalMessage: '',
                syncErrorType: 'sync',
                syncErrorTitle: 'Airbnb Sync Error',
                airbnbQualityStandardWarnModalId: 'airbnb-quality-standard-warn',
                airbnbQualityStandardSuspendModalId: 'airbnb-quality-standard-suspend',
                airbnbFetchListingStatusModalId: 'airbnb-fetch-listing-status',
                airbnbListListingModalId: 'airbnb-list-listing-modal-id',
                airbnbUnlistListingModalId: 'airbnb-unlist-listing-modal-id',
                disconnectPropertyModalId: 'disconnect-property-modal-id',
                lodgixConnectionStatusModalId: 'lodgix-connection-status-modal-id',
                turnOffInstantBookingModalId: 'turn-off-instant-booking-modal-id',
                limitedSyncError:
                    'allows_pets_as_host in booking settings should be enabled before setting PASS_THROUGH_PET_FEE',
                limitedSyncErrorMessage:
                    'If using Limited Sync, you must manually enable pet fees for the listing in Airbnb.',
                airbnbQualityStandardModalData: {
                    listingId: '',
                    restrictedCapabilities: [],
                },
                airbnbListingStatusModalData: {
                    feedShare: null,
                    node: null,
                },
                lodgixConnectionStatusModalData: {
                    feedShare: null,
                    node: null,
                    synchronizationCategory: null,
                    currentSynchronizationCategory: null,
                },
                turnOffInstantBookingModalData: {
                    feedShare: null,
                    node: null,
                    value: null,
                    oldValue: null,
                },
                selectedColumns: Object.keys(PARTNER_AIRBNB_COLUMN_MAPPING).filter(
                    (column) => defaultColumnVisibilityMapping[column] !== false
                ),
                currentSelectedRows: [],
                bulkActionDefs: [
                    {
                        label: 'Enable',
                        group: 'Marketing Title',
                        onClick: this.handleMarketingTitleBulkEnable,
                    },
                    {
                        label: 'Disable',
                        group: 'Marketing Title',
                        onClick: this.handleMarketingTitleBulkDisable,
                    },
                    {
                        label: 'Set',
                        group: 'Occupancy Taxes',
                        onClick: this.handleOccupancyTaxesBulkSetClick,
                    },
                ],
                instantBookingCategoryOptions: [],
                currentFeedShare: null,
                currentFeedShareId: null,
                currentNameFeedShare: null,
                currentNode: null,
                PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_NOT_CONNECTED,
                PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_SYNC_EVERYTHING,
                PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_LIMITED_SYNC,
                detailCellRenderer: null,
                groupDefaultExpanded: 1,
            };
        },
        beforeMount() {
            this.detailCellRenderer = 'DetailCellRenderer';
            this.groupDefaultExpanded = 1;
        },
        methods: {
            fieldBasedErrorToast({ error, field, defaultText }) {
                const { generic, fields } = formatResponseError(error);
                const errorText = fields[field] && !!fields[field].length ? fields[field][0] : generic;
                this.errorToast({
                    text: errorText || defaultText,
                });
            },
            getCancellationPolicyText(cancellationPolicy) {
                return PARTNER_AIRBNB_CANCELLATION_POLICY_MAPPING[cancellationPolicy];
            },
            getLongTermCancellationPolicyText(longTermCancellationPolicy) {
                return PARTNER_AIRBNB_LONG_TERM_CANCELLATION_POLICY_MAPPING[longTermCancellationPolicy];
            },
            getInstantBookingCategoryText(category) {
                return PARTNER_AIRBNB_INSTANT_BOOKING_CATEGORY_MAPPING[category];
            },
            isColumnHidden(column) {
                return !this.selectedColumns.includes(column);
            },
            async loadAirbnbSyncError(feedShare) {
                this.$parent.$emit('busy', true);
                try {
                    const { data } = await API.get(PARTNER_AIRBNB_FEED_SHARE_SYNC_ERROR_ENDPOINT(feedShare.id));
                    this.syncErrorModalErrorMessage = data.error_message;
                    if (this.syncErrorModalErrorMessage.includes(this.limitedSyncError)) {
                        this.syncErrorModalErrorMessage = this.limitedSyncErrorMessage;
                    }
                    this.syncErrorModalErrorDate = data.error_date_display;
                    this.syncErrorModalIsAddressError = data.is_address_error;
                    if (this.syncErrorModalIsAddressError) {
                        this.syncErrorModalAddress = data.address;
                    }
                } catch (error) {
                    this.errorToast({ text: 'Could not load airbnb sync error.' });
                    throw error;
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async handleAirbnbStatusRejectedClick(feedShare) {
                this.syncErrorTitle = 'Rejected';
                this.syncErrorType = 'reject';
                
                if (feedShare.has_sync_error) {
                    this.syncErrorType = 'sync';
                    try {
                        await this.loadAirbnbSyncError(feedShare);
                        this.$bvModal.show(this.syncErrorModalId);
                    } catch {}
                } else {
                    this.rejectedModalMessage = feedShare.airbnb_status.message;
                    this.$bvModal.show(this.syncErrorModalId);
                }
            },
            async handleAirbnbSyncErrorClick(feedShare) {
                this.syncErrorTitle = 'Airbnb Sync Error';
                this.syncErrorType = 'sync';
                
                try {
                    await this.loadAirbnbSyncError(feedShare);
                    this.$bvModal.show(this.syncErrorModalId);
                } catch {}
            },
            handleAirbnbQualityStandardWarnClick(feedShare) {
                this.airbnbQualityStandardModalData.listingId = feedShare.remote_id;
                this.$bvModal.show(this.airbnbQualityStandardWarnModalId);
            },
            handleAirbnbQualityStandardSuspendClick(feedShare) {
                this.airbnbQualityStandardModalData.listingId = feedShare.remote_id;
                this.airbnbQualityStandardModalData.restrictedCapabilities =
                    feedShare.airbnb_quality_standard.restricted_capability_types;
                this.$bvModal.show(this.airbnbQualityStandardSuspendModalId);
            },
            handleAirbnbListingStatusChange(node, feedShare, value) {
                this.airbnbListingStatusModalData.feedShare = feedShare;
                this.airbnbListingStatusModalData.node = node;
                if (value) {
                    this.$bvModal.show(this.airbnbListListingModalId);
                } else {
                    this.$bvModal.show(this.airbnbUnlistListingModalId);
                }
            },
            async listAirbnbListing() {
                this.$parent.$emit('busy', true);
                const gridApi = this.$refs.grid.gridOptions.api;
                try {
                    const { data } = await API.post(
                        PARTNER_AIRBNB_FEED_SHARE_LIST_PROPERTY(this.airbnbListingStatusModalData.feedShare.id)
                    );
                    this.airbnbListingStatusModalData.node.setData({
                        ...this.airbnbListingStatusModalData.feedShare,
                        airbnb_listing_status: data,
                    });
                    gridApi.redrawRows({ rowNodes: [this.airbnbListingStatusModalData.node] });
                    this.successToast({ text: 'Property was successfully listed on Airbnb.' });
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text: generic || 'Could not list property on Airbnb.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async unlistAirbnbListing({ reason, reasonDetails }) {
                this.$parent.$emit('busy', true);
                const gridApi = this.$refs.grid.gridOptions.api;
                try {
                    const { data } = await API.post(
                        PARTNER_AIRBNB_FEED_SHARE_UNLIST_PROPERTY(this.airbnbListingStatusModalData.feedShare.id),
                        { disabled_reason: reason, disabled_reason_details: reasonDetails }
                    );
                    this.airbnbListingStatusModalData.node.setData({
                        ...this.airbnbListingStatusModalData.feedShare,
                        airbnb_listing_status: data,
                    });
                    gridApi.redrawRows({ rowNodes: [this.airbnbListingStatusModalData.node] });
                    this.successToast({ text: 'Property was successfully unlisted on Airbnb.' });
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text: generic || 'Could not unlist property on Airbnb.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            handleLodgixConnectionStatusClick(node, feedShare, synchronizationCategory) {
                this.lodgixConnectionStatusModalData.feedShare = feedShare;
                this.lodgixConnectionStatusModalData.node = node;
                this.lodgixConnectionStatusModalData.synchronizationCategory = synchronizationCategory;
                this.lodgixConnectionStatusModalData.currentSynchronizationCategory = synchronizationCategory;
                this.$bvModal.show(this.lodgixConnectionStatusModalId);
            },
            confirmDisconnectProperty() {
                this.$bvModal.show(this.disconnectPropertyModalId);
            },
            async changeLodgixConnectionStatus() {
                this.$parent.$emit('busy', true);
                const gridApi = this.$refs.grid.gridOptions.api;
                try {
                    const { data } = await API.post(
                        PARTNER_AIRBNB_FEED_SHARE_UPDATE_SYNC_MODE_PROPERTY(
                            this.lodgixConnectionStatusModalData.feedShare.id
                        ),
                        {
                            synchronization_category:
                                this.lodgixConnectionStatusModalData.currentSynchronizationCategory,
                        }
                    );
                    this.lodgixConnectionStatusModalData.node.setData(data);
                    this.lodgixConnectionStatusModalData.node.parent.setData(data);
                    gridApi.redrawRows({ rowNodes: [this.lodgixConnectionStatusModalData.node] });
                    this.successToast({
                        text:
                            this.lodgixConnectionStatusModalData.currentSynchronizationCategory ===
                            PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_NOT_CONNECTED
                                ? 'Airbnb property was disconnected from Lodgix.'
                                : 'Synchronization category was successfully changed.',
                    });
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text:
                            generic ||
                            this.lodgixConnectionStatusModalData.currentSynchronizationCategory ===
                                PARTNER_AIRBNB_LODGIX_CONNECTION_STATUS_NOT_CONNECTED
                                ? 'Could not disconnect Airbnb property from Lodgix.'
                                : 'Could not update synchronization category.',
                    });
                    try {
                        const { data } = await this.fetchFeedById(this.lodgixConnectionStatusModalData.feedShare.id);
                        this.lodgixConnectionStatusModalData.node.setData(data);
                        this.lodgixConnectionStatusModalData.node.parent.setData(data);
                        gridApi.redrawRows({ rowNodes: [this.lodgixConnectionStatusModalData.node] });
                    } catch (e) {
                        const { generic } = formatResponseError(e);
                        this.errorToast({ text: generic || 'Could not load airbnb feed.' });
                    }
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async turnOffInstantBooking() {
                this.turnOffInstantBookingModalData.node.setData({
                    ...this.turnOffInstantBookingModalData.feedShare,
                    instant_booking_allowed_category: this.turnOffInstantBookingModalData.value,
                });
                try {
                    await API.patch(
                        PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(this.turnOffInstantBookingModalData.feedShare.id),
                        { instant_booking_allowed_category: this.turnOffInstantBookingModalData.value }
                    );
                } catch (e) {
                    this.turnOffInstantBookingModalData.node.setData({
                        ...this.turnOffInstantBookingModalData.feedShare,
                        instant_booking_allowed_category: this.turnOffInstantBookingModalData.oldValue,
                    });
                    this.fieldBasedErrorToast({
                        error: e,
                        field: 'instant_booking_allowed_category',
                        defaultText: 'Could not save instant booking.',
                    });
                }
            },
            async handleAirbnbNameClick(feedShare, airbnbPropertyName) {
                feedShare.airbnbPropertyName = extractHTMLContent(airbnbPropertyName);
                this.currentNameFeedShare = feedShare;
                this.$bvModal.show(this.nameModalId);
            },
            async fetchData() {
                try {
                    const { data } = await API.get(PARTNER_AIRBNB_DATA_ENDPOINT());
                    this.allowAirbnb = data.allow_airbnb;
                    this.allowAirbnbRequest = data.allow_airbnb_request;
                    this.allowDisconnect = data.allow_disconnect;
                    this.airbnbClientId = data.airbnb_client_id;
                    this.airbnbRedirectURL = data.airbnb_redirect_url;
                    this.instantBookingCategoryOptions = data.instant_booking_category_options;
                } catch {
                    this.errorToast({ text: 'Could not load airbnb data.' });
                }
            },
            async fetchFeed() {
                this.currentSelectedRows = [];
                this.currentFeedShare = null;

                try {
                    const { data } = await API.get(PARTNER_AIRBNB_FEED_SHARE_FEED_ENDPOINT());
                    this.feed = data;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not load airbnb feed.' });
                }
            },
            async refetchFeed() {
                this.$parent.$emit('busy', true);
                try {
                    await this.fetchFeed();
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            fetchFeedById(feedShareId) {
                return API.get(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(feedShareId));
            },
            async refetchFeedById(feedShareId) {
                this.$parent.$emit('busy', true);
                try {
                    const { data } = await this.fetchFeedById(feedShareId);
                    this.currentNode.setData(data);
                    if (this.currentNode.detailNode) {
                        const gridApi = this.$refs.grid.gridOptions.api;
                        gridApi.redrawRows({ rowNodes: [this.currentNode.detailNode] });
                    }
                    this.currentNode = null;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not load airbnb feed.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            updateFeed(data) {
                this.currentNode.setData(data);
                if (this.currentNode.detailNode) {
                    const gridApi = this.$refs.grid.gridOptions.api;
                    gridApi.redrawRows({ rowNodes: [this.currentNode.detailNode] });
                }
                this.currentNode = null;
            },
            async fetchPartnerSettings() {
                try {
                    const { data } = await API.get(PARTNER_AIRBNB_SETTINGS_ENDPOINT());
                    this.partnerSettings = data;
                    this.accountId = data.account_id;
                    this.feedEnabled = data.feed_enabled;
                    this.feedEnabledOn = data.feed_enabled_on_display;
                    this.messagingApiAllowed = data.messaging_api_allowed;
                    this.addDamageToCleaningFee = data.add_damage_to_cleaning_fee;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not load partner settings.' });
                }
            },
            async requestAirbnbAccess() {
                this.busy = true;
                this.$parent.$emit('busy', true);
                try {
                    await API.post(PARTNER_AIRBNB_REQUEST_ACCESS_ENDPOINT());
                    await this.load({ withBusy: false });
                    this.$bvModal.hide(this.requestAirbnbAccessModalId);
                    this.successToast({ text: 'Airbnb access request was sent.' });
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text: generic || 'Could not request airbnb access.',
                    });
                } finally {
                    this.busy = false;
                    this.$parent.$emit('busy', false);
                }
            },
            handleSelectionChange() {
                const gridApi = this.$refs.grid.gridOptions.api;
                this.currentSelectedRows = gridApi.getSelectedRows();
                this.currentFeedShare = null;
            },
            handleSettingsClick() {
                this.$bvModal.show(this.settingsModalId);
            },
            handleReviewsClick() {
                this.$router.push('/app/partner/reviews/');
            },
            handleOccupancyTaxesCellClick({ data }) {
                this.currentFeedShare = data;
                this.$bvModal.show(this.occupancyTaxesModalId);
            },
            handleOccupancyTaxesBulkSetClick() {
                this.$bvModal.show(this.occupancyTaxesModalId);
            },
            async setOccupancyTaxes(ids) {
                if (!!this.currentFeedShare) {
                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(this.currentFeedShare.id), {
                        occupancy_tax_ids: ids,
                    });
                } else if (this.hasSelectedRows) {
                    await API.post(PARTNER_AIRBNB_FEED_SHARE_BULK_OCCUPANCY_TAX_SET(), {
                        feed_share_ids: this.currentSelectedRows.map((row) => row.id),
                        occupancy_tax_ids: ids,
                    });
                }
            },
            async handleSetOccupancyTaxesSuccess() {
                await this.refetchFeed();
                this.occupancyTaxesModalKey += 1;
            },
            connectAirbnb() {
                window.location = [
                    'https://www.airbnb.com/oauth2/auth?',
                    'client_id=',
                    this.airbnbClientId,
                    '&redirect_uri=',
                    encodeURIComponent(this.airbnbRedirectURL),
                    '&scope=',
                    'property_management,messages_read,messages_write',
                ].join('');
            },
            disconnect() {
                return API.post(PARTNER_AIRBNB_DISCONNECT_ENDPOINT());
            },
            handleDisconnectClick() {
                this.$bvModal.show(this.disconnectModalId);
            },
            handleDisconnectSuccess() {
                this.load();
            },
            handleSettingsSaveSuccess() {
                this.successToast({
                    text: 'Settings were successfully saved.',
                });
                this.load();
            },
            handleSettingsHidden() {
                this.settingsModalKey++;
            },
            handleRequestAirbnbAccessClick() {
                this.$bvModal.show(this.requestAirbnbAccessModalId);
            },
            handleConnectAirbnbClick() {
                this.$bvModal.show(this.connectAirbnbModalId);
            },
            handleInfoClick() {
                this.$bvModal.show(this.infoModalId);
            },
            handleAgreementClick() {
                this.$bvModal.show(this.agreementModalId);
            },
            async handleMarketingTitleBulkEnable() {
                this.$parent.$emit('busy', true);
                try {
                    await API.post(PARTNER_AIRBNB_FEED_SHARE_BULK_MARKETING_TITLE_ENABLE(), {
                        feed_share_ids: this.currentSelectedRows.map((row) => row.id),
                    });
                    await this.fetchFeed();
                } catch {
                    this.errorToast({ text: 'Could not update marketing title.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async handleMarketingTitleBulkDisable() {
                this.$parent.$emit('busy', true);
                try {
                    await API.post(PARTNER_AIRBNB_FEED_SHARE_BULK_MARKETING_TITLE_DISABLE(), {
                        feed_share_ids: this.currentSelectedRows.map((row) => row.id),
                    });
                    await this.fetchFeed();
                } catch {
                    this.errorToast({ text: 'Could not update marketing title.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async handleSaveCustomName(value) {
                this.$parent.$emit('busy', true);
                this.$bvModal.hide(this.nameModalId);

                try {
                    await API.patch(PARTNER_AIRBNB_FEED_SHARE_BY_ID_ENDPOINT(this.currentNameFeedShare.id), {
                        custom_property_name: value,
                    });

                    await this.fetchFeed();
                } catch {
                    this.errorToast({ text: 'Could not save Custom Airbnb Property Name.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            handleRefreshClick() {
                this.refetchFeed();
            },
            async load({ withBusy = true } = {}) {
                if (withBusy) {
                    this.busy = true;
                    this.$parent.$emit('busy', true);
                }
                try {
                    await Promise.all([this.fetchData(), this.fetchPartnerSettings()]);

                    if (this.feedEnabled) {
                        await this.fetchFeed();
                    }
                } finally {
                    if (withBusy) {
                        this.busy = false;
                        this.$parent.$emit('busy', false);
                    }
                }
            },
        },
        watch: {
            selectedColumns(newValue, oldValue) {
                difference(newValue, oldValue).forEach((columnVisible) => {
                    this.$store.commit('partnerAirbnbColumnsLocalStorage/showColumn', columnVisible);
                });
                difference(oldValue, newValue).forEach((columnHidden) => {
                    this.$store.commit('partnerAirbnbColumnsLocalStorage/hideColumn', columnHidden);
                });
            },
        },
    };
</script>
